/**
 * Sözleşme sözleşmeleri (M3 WP 3.8). Kaynak: keşif tur 2 F09 (aşamalar, aşamaya bağlı tarihler, silme yalnız Hazırlanıyor,
 * tekliften sözleşme), ÜK-4 (damga vergisi tenant ayarı, varsayılan kapalı, oran girilir, sürümlü), ÜK-7 (DOCX şablon →
 * LibreOffice PDF: `contract-docx.ts`). Castinflow e-imza SUNMAZ: "İmzalandı" durumu imza tarihinin elle kaydıdır, hukuki bağlayıcılık
 * iddiası taşımaz.
 */
import { z } from 'zod';
import { MONEY_POLICY, currencySchema, decimalString } from './decimal.js';
const uuid = z.guid();
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'tarih YYYY-AA-GG olmalı');
const optText = (max) => z
    .string()
    .trim()
    .max(max)
    .transform((s) => (s === '' ? null : s))
    .nullable();
export const CONTRACT_STATUSES = [
    'preparing',
    'awaiting_signature',
    'signed',
    'terminated',
    'completed',
];
export const CONTRACT_STATUS_LABELS = {
    preparing: 'Hazırlanıyor',
    awaiting_signature: 'İmza bekliyor',
    signed: 'İmzalandı',
    terminated: 'Feshedildi',
    completed: 'Tamamlandı',
};
/**
 * Geçişler (Castinflow kararı K-M3-12; keşif F09 aşama diyaloğu): Hazırlanıyor → İmza bekliyor (içerik revizyonu kilitlenir);
 * İmza bekliyor → İmzalandı (imza tarihi zorunlu) | Hazırlanıyor (değişiklik için; yeni revizyon); İmzalandı → Feshedildi (fesih
 * tarihi + sebep) | Tamamlandı; Feshedildi/Tamamlandı son durumdur.
 */
export const CONTRACT_STATUS_TRANSITIONS = {
    preparing: ['awaiting_signature'],
    awaiting_signature: ['signed', 'preparing'],
    signed: ['terminated', 'completed'],
    terminated: [],
    completed: [],
};
/** Sözleşme belge numarası (`SZL-00012`); kayıt numarası yoksa yalnız önek. Profil, arama belgesi ve sonuç başlığı bunu kullanır. */
export function contractDocumentNo(recordNo) {
    if (recordNo === null || recordNo === undefined)
        return 'SZL';
    return `SZL-${String(recordNo).padStart(5, '0')}`;
}
export const CONTRACT_TERMINATION_REASONS = [
    { key: 'breach', label: 'Sözleşme ihlali' },
    { key: 'mutual', label: 'Karşılıklı anlaşma' },
    { key: 'customer_request', label: 'Müşteri talebi' },
    { key: 'other', label: 'Diğer' },
];
/**
 * G3 (sahip kararı B, 24 Ekim 2026; migration 0290): sözleşme tutarının vergi anlamı. `null` = Bilinmiyor (eski kayıtlar
 * varsayımla doldurulmaz). Tutar, para birimi ve damga vergisi hesabı değişmez; alan yalnız tutarın anlamını kaydeder.
 */
export const CONTRACT_TAX_BASES = ['vat_included', 'vat_excluded'];
export const CONTRACT_TAX_BASIS_LABELS = {
    vat_included: 'KDV dahil',
    vat_excluded: 'KDV hariç',
    unknown: 'Bilinmiyor',
};
export const contractTaxBasisLabel = (v) => CONTRACT_TAX_BASIS_LABELS[v ?? 'unknown'];
/** Yaklaşan bitiş uyarısı gün aralığı (çalışma alanı ayarı; boş = ürün varsayılanı `CONTRACT_EXPIRY_NOTICE_DAYS`) */
export const CONTRACT_EXPIRY_NOTICE_RANGE = { min: 1, max: 365 };
export const contractCoreInputSchema = z
    .object({
    name: z.string().trim().min(1, 'ad gerekli').max(200),
    companyId: uuid,
    contactId: uuid.nullable().optional(),
    opportunityId: uuid.nullable().optional(),
    amount: decimalString(MONEY_POLICY).nullable().optional(),
    currency: currencySchema.optional(),
    /** Tutarın vergi anlamı; `null` = Bilinmiyor */
    amountTaxBasis: z.enum(CONTRACT_TAX_BASES).nullable().optional(),
    startDate: isoDate.nullable().optional(),
    endDate: isoDate.nullable().optional(),
    details: optText(10000).optional(),
    ownerId: uuid.optional(),
})
    .strict();
export const createContractInputSchema = z
    .object({
    core: contractCoreInputSchema,
    /** Tekliften/siparişten oluşturma (müşteri, kişi, fırsat, tutar kaynaktan önerilir; çağıran değerleri esastır) */
    sourceProposalId: uuid.nullable().optional(),
    sourceOrderId: uuid.nullable().optional(),
    customValues: z.record(z.string(), z.unknown()).prefault({}),
})
    .strict();
export const updateContractInputSchema = z
    .object({ core: contractCoreInputSchema.partial() })
    .strict();
/** Takvim günü (YYYY-MM-DD) — verilen IANA saat diliminde "bugün" */
export function localIsoDate(timeZone, now) {
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(now);
    const g = (t) => parts.find((p) => p.type === t)?.value ?? '';
    return `${g('year')}-${g('month')}-${g('day')}`;
}
export const CONTRACT_SIGNED_ON_FUTURE = 'İmza tarihi ileri bir tarih olamaz';
/**
 * K-M7-50.4 ÜRÜN KARARI: sözleşmenin imza tarihi GELECEKTE olamaz — "İmzalandı" geçmişte ya da bugün olmuş bir olayın
 * kaydıdır (ileri tarihli yürürlük `startDate` ile ifade edilir). "Bugün" çalışma alanının saat diliminde (`tenants.timezone`,
 * IANA) takvim günüdür; sunucu kuralı esastır, arayüz aynı işlevle önceden uyarır. Uygunsa `null`.
 */
export function contractSignedOnError(signedOn, timeZone, now) {
    return signedOn > localIsoDate(timeZone, now) ? CONTRACT_SIGNED_ON_FUTURE : null;
}
export const contractStatusInputSchema = z
    .object({
    status: z.enum(CONTRACT_STATUSES),
    /** İmzalandı: imza tarihi (zorunlu) */
    signedOn: isoDate.optional(),
    /** Feshedildi: fesih tarihi (zorunlu) + sebep */
    terminatedOn: isoDate.optional(),
    terminationReasonKey: z
        .string()
        .regex(/^[a-z][a-z0-9_]{1,39}$/)
        .optional(),
    note: optText(2000).optional(),
    /** İmza bekliyor: kilitlenen revizyonun PDF'i bu şablonla hazırlansın (ÜK-7; isteğe bağlı) */
    templateId: uuid.optional(),
})
    .strict();
export const contractSettingsSchema = z.object({
    stampTaxEnabled: z.boolean(),
    /** Binde oran (ör. 9.48); kapalıyken yok sayılır */
    stampTaxPerMille: z.string().nullable(),
    version: z.number().int(),
    /**
     * G3: yaklaşan bitiş uyarısının GEÇERLİ gün sayısı ve kaynağı (`default` = ürün varsayılanı 30 gün, `tenant` = çalışma alanı
     * ayarı). Alan yoksa önceki sunucu sürümü (istemci varsayılanı kullanır).
     */
    expiryNoticeDays: z.number().int().min(1).optional(),
    expiryNoticeSource: z.enum(['default', 'tenant']).optional(),
});
export const updateContractSettingsInputSchema = z
    .object({
    expectedVersion: z.number().int().min(0),
    stampTaxEnabled: z.boolean(),
    stampTaxPerMille: decimalString({
        maxIntDigits: 3,
        maxScale: 4,
        min: '0',
        max: '100',
    }).nullable(),
    /** G3: yaklaşan bitiş uyarısı (gün); `null` = ürün varsayılanına dön; verilmezse değişmez */
    expiryNoticeDays: z
        .number()
        .int('tam gün girin')
        .min(CONTRACT_EXPIRY_NOTICE_RANGE.min, 'en az 1 gün')
        .max(CONTRACT_EXPIRY_NOTICE_RANGE.max, 'en çok 365 gün')
        .nullable()
        .optional(),
})
    .strict();
export const contractProfileSchema = z.object({
    kind: z.literal('contract'),
    documentNo: z.string(),
    name: z.string(),
    companyId: z.string().nullable(),
    contactId: z.string().nullable(),
    opportunityId: z.string().nullable(),
    sourceProposalId: z.string().nullable(),
    sourceOrderId: z.string().nullable(),
    status: z.enum(CONTRACT_STATUSES),
    amount: z.string().nullable(),
    currency: z.string(),
    /** G3: tutarın vergi anlamı (`null` = Bilinmiyor); alan yoksa önceki sunucu sürümü */
    amountTaxBasis: z.enum(CONTRACT_TAX_BASES).nullable().optional(),
    startDate: z.string().nullable(),
    endDate: z.string().nullable(),
    signedOn: z.string().nullable(),
    terminatedOn: z.string().nullable(),
    terminationReasonKey: z.string().nullable(),
    currentRevisionNo: z.number().int(),
    /** Kilitli (imzaya sunulan) revizyonun damga vergisi — ayar kapalıysa `null` */
    stampTax: z.string().nullable(),
    details: z.string().nullable(),
});
export const contractRevisionSchema = z.object({
    revisionNo: z.number().int(),
    lockedAt: z.string().nullable(),
    snapshot: z.record(z.string(), z.unknown()),
    stampTax: z.string().nullable(),
    stampTaxPerMille: z.string().nullable(),
    createdAt: z.string(),
});
//# sourceMappingURL=contracts.js.map